import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Btn } from "../components/common";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Users2 } from "lucide-react";

const TYPE_LABEL = { booking: "Booking", visit: "Kunjungan", payment_confirmation: "Konfirmasi Bayar", ticket: "Tiket", document_upload: "Unggah Dokumen", favorite: "Favorit" };

export default function AktivitasApp() {
  const [data, setData] = useState({ items: [], daily_summary: [], warnings: {} });
  const [type, setType] = useState("");
  const [users, setUsers] = useState([]);
  const [pulling, setPulling] = useState(false);

  const load = useCallback(() => {
    api.get("/ajl/aktivitas", { params: type ? { type } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [type]);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "Sales"))).catch(() => {}); }, [load]);

  const pull = async () => {
    setPulling(true);
    try { const r = await api.post("/ajl/pull"); toast.success(r.data.skipped ? "URL AJL belum diset" : "Penarikan selesai"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setPulling(false); }
  };

  const assign = (a, name) => api.post(`/ajl/aktivitas/${a.id}/assign`, { user_name: name })
    .then(() => { toast.success("Ditugaskan"); load(); }).catch((e) => toast.error(errMsg(e)));

  const w = data.warnings || {};
  return (
    <div data-testid="aktivitas-app-page">
      <PageHeader title="Aktivitas ABAD JAYA LIVING" subtitle="Log aktivitas konsumen dari aplikasi — booking, kunjungan, konfirmasi bayar, tiket" testid="aktivitas-header">
        <Btn onClick={pull} disabled={pulling} data-testid="aktivitas-pull-btn"><RefreshCw className={`w-4 h-4 ${pulling ? "animate-spin" : ""}`} /> Tarik sekarang</Btn>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <Card testid="warn-stale" className={w.unanswered_24h ? "border-amber-500/50" : ""}>
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-slate-300">Lead belum direspons &gt;24 jam</span></div>
          <p className="text-2xl font-semibold text-amber-300 mt-1" data-testid="warn-stale-count">{w.unanswered_24h || 0}</p>
        </Card>
        <Card testid="warn-dup" className={(w.duplicate_unit_bookings || []).length ? "border-rose-500/50" : ""}>
          <div className="flex items-center gap-2"><Users2 className="w-4 h-4 text-rose-400" /><span className="text-sm text-slate-300">Unit dibooking &gt;1 konsumen</span></div>
          <p className="text-2xl font-semibold text-rose-300 mt-1" data-testid="warn-dup-count">{(w.duplicate_unit_bookings || []).length}</p>
        </Card>
      </div>

      {data.daily_summary.length > 0 && (
        <Card className="mb-4" testid="aktivitas-daily">
          <p className="text-xs text-slate-400 mb-2">Ringkasan 7 hari terakhir</p>
          <div className="flex flex-wrap gap-2">
            {data.daily_summary.map((d) => (
              <div key={d.date} className="text-[11px] border border-slate-800 rounded-lg px-3 py-2">
                <span className="text-slate-500 font-mono">{d.date}</span>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {Object.entries(d).filter(([k]) => k !== "date").map(([k, v]) => (
                    <Badge key={k} tone="cyan">{TYPE_LABEL[k] || k}: {v}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "booking", "visit", "payment_confirmation", "ticket", "document_upload", "favorite"].map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`aktivitas-filter-${t || "semua"}`}
            className={`text-xs px-3.5 py-2 rounded-lg border transition-colors ${type === t ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900"}`}>
            {t ? (TYPE_LABEL[t] || t) : "Semua"}
          </button>
        ))}
      </div>

      <div className="space-y-2" data-testid="aktivitas-list">
        {data.items.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada aktivitas tersedot dari aplikasi.</p></Card>}
        {data.items.map((a) => (
          <Card key={a.id || a.ext_id} className="!p-4" testid={`aktivitas-${a.id || a.ext_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="emerald">{TYPE_LABEL[a.type] || a.type}</Badge>
              <span className="text-slate-200 text-sm flex-1 min-w-[160px]">{a.summary || "-"}</span>
              <span className="text-[11px] text-slate-500 font-mono">{(a.at || "").replace("T", " ").slice(0, 16)}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {(a.konsumen || {}).name || "-"} · {(a.konsumen || {}).phone || "-"} {a.status && `· status: ${a.status}`}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[11px] text-slate-500">Ditangani oleh:</span>
              <select className="bg-slate-900 border border-slate-800 rounded-lg text-xs px-2 py-1.5 text-slate-300"
                value={a.handled_by || ""} onChange={(e) => assign(a, e.target.value)} data-testid={`aktivitas-assign-${a.id || a.ext_id}`}>
                <option value="">— belum —</option>
                {[...new Set([a.handled_by, ...users.map((u) => u.name)].filter(Boolean))].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
